import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { 
  CheckCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'
import { RootState, AppDispatch } from '../../store'
import { getSessionStatus } from '../../store/slices/testSlice'
import { formatDistanceToNow } from 'date-fns'
// testCompletionService removed - MySQL is the source of truth for assessment completion

interface TestResult {
  status: string
  assessmentName: string
}

const TestCompletedPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  
  const { currentSession } = useSelector((state: RootState) => state.test)
  const { user } = useSelector((state: RootState) => state.auth)
  const { remainingTimeSeconds, totalDurationMinutes, isRunning } = useSelector((state: RootState) => state.timer)
  
  const [testResult, setTestResult] = useState<TestResult | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const loadTestResults = useCallback(async () => {
    try {
      setIsLoading(true)
      
      // Get session status
      const result = await dispatch(getSessionStatus(sessionId!))
      const sessionData = result.payload
      
      console.log('Session data received:', sessionData)
      console.log('Current session from Redux:', currentSession)
      
      // Calculate actual time taken
      let timeTakenSeconds = 0
      let timerCalculationUsed = false // Flag to prevent overrides
      
      // Try to get start time from various possible fields
      let startTimeField = sessionData?.start_time || (currentSession as any)?.start_time
      let submitTimeField = sessionData?.submit_time || (currentSession as any)?.submit_time
      
      // If we have started_at (timestamp) instead of start_time (ISO string), convert it
      if (!startTimeField && sessionData?.started_at) {
        if (typeof sessionData.started_at === 'number') {
          // Convert timestamp to ISO string
          startTimeField = new Date(sessionData.started_at * 1000).toISOString()
        } else {
          startTimeField = sessionData.started_at
        }
      }
      
      console.log('Time fields:', { startTimeField, submitTimeField })
      console.log('Session data keys:', Object.keys(sessionData || {}))
      console.log('Current session keys:', Object.keys(currentSession || {}))
      console.log('All time-related fields from sessionData:', {
        start_time: sessionData?.start_time,
        started_at: sessionData?.started_at,
        submit_time: sessionData?.submit_time,
        created_at: sessionData?.created_at
      })
      
      console.log('Timer state from Redux:', {
        remainingTimeSeconds,
        totalDurationMinutes,
        isRunning
      })
      
      console.log('Session data duration info:', {
        sessionData_duration_minutes: sessionData?.duration_minutes,
        sessionData_remaining_time_seconds: sessionData?.remaining_time_seconds,
        currentSession_duration_minutes: (currentSession as any)?.duration_minutes,
        currentSession_remaining_time_seconds: (currentSession as any)?.remaining_time_seconds
      })
      
      if (startTimeField && submitTimeField) {
        // Calculate from actual start and submit times
        const startTime = new Date(startTimeField).getTime()
        const endTime = new Date(submitTimeField).getTime()
        
        // Validate that times are valid
        if (!isNaN(startTime) && !isNaN(endTime) && endTime > startTime) {
          timeTakenSeconds = Math.floor((endTime - startTime) / 1000)
          console.log('Calculated time from start_time and submit_time:', { 
            startTimeField, 
            submitTimeField, 
            startTime, 
            endTime, 
            timeTakenSeconds 
          })
        } else {
          console.warn('Invalid time values:', { startTime, endTime })
        }
      }
      
      // If we couldn't calculate from start/submit times, try duration - remaining time
      if (timeTakenSeconds === 0 && sessionData?.duration_minutes && sessionData?.remaining_time_seconds !== undefined && sessionData?.remaining_time_seconds !== null) {
        timeTakenSeconds = (sessionData.duration_minutes * 60) - sessionData.remaining_time_seconds
        console.log('Calculated time from duration - remaining:', { 
          duration_minutes: sessionData.duration_minutes, 
          remaining_time_seconds: sessionData.remaining_time_seconds, 
          timeTakenSeconds 
        })
      }
      
      // If still no time calculated, try to get from currentSession
      if (timeTakenSeconds === 0 && currentSession) {
        const currentStartTime = (currentSession as any)?.start_time
        const currentSubmitTime = (currentSession as any)?.submit_time
        
        if (currentStartTime && currentSubmitTime) {
          const startTime = new Date(currentStartTime).getTime()
          const endTime = new Date(currentSubmitTime).getTime()
          
          if (!isNaN(startTime) && !isNaN(endTime) && endTime > startTime) {
            timeTakenSeconds = Math.floor((endTime - startTime) / 1000)
            console.log('Calculated time from currentSession:', { 
              currentStartTime, 
              currentSubmitTime, 
              timeTakenSeconds 
            })
          }
        }
      }
      
      // PRIORITY: Try to get from timer state first (most accurate for real-time tracking)
      // Use Redux timer state if available, otherwise fall back to session data
      const timerDuration = totalDurationMinutes || sessionData?.duration_minutes
      const timerRemaining = remainingTimeSeconds !== undefined ? remainingTimeSeconds : sessionData?.remaining_time_seconds
      
      if (timerDuration && timerRemaining !== undefined && timerRemaining >= 0) {
        const calculatedTime = (timerDuration * 60) - timerRemaining
        // Only use timer calculation if it's reasonable (not negative, not too large)
        if (calculatedTime > 0 && calculatedTime <= timerDuration * 60) {
          timeTakenSeconds = calculatedTime
          timerCalculationUsed = true // Mark that we used timer calculation
          console.log('✅ Using timer state calculation (MOST ACCURATE):', { 
            timerDuration, 
            timerRemaining, 
            timeTakenSeconds,
            calculation: `${timerDuration * 60} - ${timerRemaining} = ${calculatedTime}`,
            source: totalDurationMinutes ? 'Redux' : 'SessionData'
          })
          
          // IMPORTANT: Once we have a valid timer calculation, don't let any other logic override it
          console.log('🔒 Timer calculation is valid, preventing any overrides')
        } else {
          console.log('⚠️ Timer calculation seems invalid, skipping:', {
            timerDuration,
            timerRemaining,
            calculatedTime
          })
        }
      }
      
      // If still no time calculated, try to estimate from session duration
      if (!timerCalculationUsed && timeTakenSeconds === 0 && sessionData?.duration_minutes) {
        // If the test was completed, assume it took most of the allocated time
        // This is a reasonable estimate for completed tests
        timeTakenSeconds = Math.floor(sessionData.duration_minutes * 60 * 0.8) // 80% of allocated time
        console.log('Estimated time from session duration:', { 
          duration_minutes: sessionData.duration_minutes, 
          timeTakenSeconds 
        })
      }
      
      // DEBUG: Log the current timeTakenSeconds before any final overrides
      console.log('Time taken before final checks:', timeTakenSeconds, 'seconds')
      
      // Ensure we have a minimum time of 1 second if test was completed
      if (!timerCalculationUsed && timeTakenSeconds === 0 && (sessionData?.status === 'submitted' || sessionData?.status === 'auto_submitted')) {
        timeTakenSeconds = 1 // Show at least 1 second for completed tests
        console.log('Set minimum time for completed test:', timeTakenSeconds)
      }
      
      console.log('Final calculated time taken:', timeTakenSeconds, 'seconds')
      console.log('Timer calculation used:', timerCalculationUsed)
      
      // Get assessment name (remove the "assessment_" prefix if present)
      let assessmentName = sessionData?.assessment_title || 'Assessment'
      if (assessmentName.startsWith('assessment_')) {
        assessmentName = 'Assessment'
      }
      
      // Display appropriate status
      let displayStatus = sessionData?.status || 'completed'
      // Map technical statuses to user-friendly ones
      if (displayStatus === 'submitted' || displayStatus === 'auto_submitted') {
        displayStatus = 'Completed'
      } else if (displayStatus === 'started') {
        // If session shows as started but we're on completed page, it was likely just submitted
        displayStatus = 'Completed'
      }
      
      setTestResult({
        status: displayStatus,
        assessmentName: assessmentName
      })
      
      // Mark assessment as completed in the test completion service
      if (user?.email && sessionData) {
        try {
          // Always construct the correct assessment ID format: "driveId_assessmentType"
          let assessmentId = sessionData.assessment_id || sessionData.assessment?.id || ''
          
          // Always construct the ID properly if we have drive_id and assessment_type
          if (sessionData.drive_id && sessionData.assessment_type) {
            assessmentId = `${sessionData.drive_id}_${sessionData.assessment_type}`
            console.log('Constructed assessment ID:', assessmentId, 'from drive_id:', sessionData.drive_id, 'and assessment_type:', sessionData.assessment_type)
          } else {
            console.warn('Missing drive_id or assessment_type, using raw assessment_id:', assessmentId)
          }
          
          // Completion status is tracked in MySQL via assessment_responses table
          // No need for localStorage - MySQL is the source of truth
          console.log('Assessment completed for user:', user.email, 'with ID:', assessmentId, '- tracked in MySQL')
        } catch (error) {
          console.error('Failed to mark assessment as completed:', error)
        }
      }
      
    } catch (error) {
      console.error('Failed to load test results:', error)
    } finally {
      setIsLoading(false)
    }
  }, [sessionId, dispatch, currentSession, remainingTimeSeconds, totalDurationMinutes, isRunning])

  useEffect(() => {
    if (!sessionId) {
      navigate('/dashboard')
      return
    }

    loadTestResults()
  }, [sessionId, loadTestResults, navigate])



  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading test results...</p>
        </div>
      </div>
    )
  }

  if (!testResult || !currentSession) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Test results not found
          </h2>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn btn-primary"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="mx-auto w-14 h-14 bg-success-100 rounded-full flex items-center justify-center mb-3">
            <CheckCircleIcon className="w-7 h-7 text-success-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            Test Completed!
          </h1>
          <p className="text-sm text-gray-600">
            {testResult.assessmentName}
          </p>
        </div>

        {/* Test Details */}
        <div className="max-w-xl mx-auto mb-6">
          <div className="card p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-3">
              Test Details
            </h3>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Assessment:</span>
                <span className="font-medium">{testResult.assessmentName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className="font-medium capitalize text-success-600">
                  {testResult.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Submitted:</span>
                <span className="font-medium">
                  {(currentSession as any).submit_time 
                    ? formatDistanceToNow(new Date((currentSession as any).submit_time), { addSuffix: true })
                    : 'Just now'
                  }
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="btn btn-primary flex items-center space-x-2 mx-auto"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default TestCompletedPage
